<template>
  <form class="flex flex-col w-full" @submit.prevent="onSubmit">
    <div
      class="px-6 py-5 flex flex-col gap-6 max-h-[calc(100vh-250px)] overflow-y-auto"
    >
      <div class="grid grid-cols-2 gap-6">
        <input-v2
          v-model.trim="botName"
          :label="$t('D_ASSISTANCE.SETTINGS.BOT_NAME_LABEL')"
          :placeholder="$t('D_ASSISTANCE.SETTINGS.BOT_NAME_PLACEHOLDER')"
          :disabled="!hasEditPermission('ChatGPT')"
        />
        <select-v2
          v-model="engine"
          :options="engineOptions"
          :label="$t('D_ASSISTANCE.SETTINGS.ENGINE_LABEL')"
          :disabled="!hasEditPermission('ChatGPT')"
        />
      </div>

      <div class="h-px bg-gray-200 shrink-0" />

      <div>
        <div class="text-sm leading-5 font-semibold text-gray-800">
          {{ $t('D_ASSISTANCE.SETTINGS.FLAGS') }}
        </div>
        <div class="text-sm leading-5 text-gray-600 mt-2">
          {{ $t('D_ASSISTANCE.SETTINGS.FLAGS_DESC') }}
        </div>
        <div
          class="bg-gray-50 rounded-[8px] py-4 px-3 mt-3 flex flex-col gap-3"
        >
          <div
            v-for="(text, index) in flagItems"
            :key="index"
            class="flex flex-row items-center gap-2"
          >
            <span
              class="w-[22px] h-[22px] shrink-0 rounded-[8px] border border-gray-300 bg-gray-100 text-xs text-gray-600 flex items-center justify-center"
            >
              {{ index + 1 }}
            </span>
            <span class="text-sm leading-5 text-gray-600">{{ text }}</span>
          </div>
        </div>
      </div>

      <input-v2
        id="free-text"
        v-model="freeText"
        multiline
        auto-resize
        :label="$t('D_ASSISTANCE.SETTINGS.FREE_TEXT_LABEL')"
        :hint="$t('D_ASSISTANCE.SETTINGS.MAX_CHARACTERS_HINT')"
        :placeholder="$t('D_ASSISTANCE.SETTINGS.FREE_TEXT_PLACEHOLDER')"
        :disabled="!hasEditPermission('ChatGPT')"
      />

      <div class="h-px bg-gray-200 shrink-0" />

      <div class="rounded-[12px] border border-gray-200 px-[11px] py-4">
        <div class="text-base leading-6 font-semibold text-gray-700">
          {{ $t('D_ASSISTANCE.SETTINGS.URLS_LABEL') }}
        </div>
        <div class="text-sm leading-5 text-gray-500 mt-0.5">
          {{ $t('D_ASSISTANCE.SETTINGS.URLS_DESC') }}
        </div>
        <div
          v-for="(url, index) in urls"
          :key="index"
          class="flex flex-row gap-2 mt-4"
        >
          <input-v2
            :id="'url-' + index"
            v-model="urls[index]"
            class="flex-1"
            :dir="url ? 'ltr' : null"
            :placeholder="$t('D_ASSISTANCE.SETTINGS.URL_PLACEHOLDER')"
            :disabled="!hasEditPermission('ChatGPT')"
          />
          <button-v2
            v-if="hasEditPermission('ChatGPT')"
            variant="ghost"
            color="danger"
            icon="delete"
            class="!w-11 !h-11"
            :aria-label="$t('D_ASSISTANCE.SETTINGS.REMOVE_URL')"
            @click="removeUrl(index)"
          />
        </div>
        <button-v2
          v-if="isAddUrlAvailable"
          variant="outline"
          icon="add"
          class="mt-4 !text-base"
          @click="addUrl"
        >
          {{ $t('D_ASSISTANCE.SETTINGS.ADD_URL_BUTTON_LABEL') }}
        </button-v2>
      </div>

      <div class="rounded-[12px] border border-gray-200 px-[11px] py-4">
        <div class="text-base leading-6 font-semibold text-gray-700">
          {{ $t('D_ASSISTANCE.SETTINGS.UPLOAD_TITLE') }}
        </div>
        <div class="text-sm leading-5 text-gray-500 mt-0.5">
          {{ $t('D_ASSISTANCE.SETTINGS.UPLOAD_DESC') }}
        </div>
        <file-upload
          v-if="isAddFileAvailable"
          ref="upload"
          class="!block w-full"
          :multiple="true"
          :drop="true"
          :drop-directory="false"
          @input-file="onIndirectFileUpload"
        >
          <div
            class="border-2 border-dashed border-[#DFDCDC] rounded-[20px] h-[98px] flex flex-col items-center justify-center mt-4 cursor-pointer"
          >
            <div class="text-base leading-6 text-primary">
              {{ $t('D_ASSISTANCE.SETTINGS.DROPZONE_TITLE') }}
            </div>
            <div class="text-xs leading-[18px] text-neutral-500 mt-2.5">
              {{ dropzoneHint }}
            </div>
          </div>
        </file-upload>
        <div v-if="attachedFiles.length" class="attachment-preview-box mt-3">
          <attachment-preview
            :attachments="attachedFiles"
            :remove-attachment="removeAttachment"
            :readonly="!hasEditPermission('ChatGPT')"
          />
        </div>
      </div>
    </div>

    <modal-footer-v2
      :cancel-text="$t('D_ASSISTANCE.SETTINGS.CANCEL')"
      :confirm-text="$t('D_ASSISTANCE.SETTINGS.APPLY')"
      confirm-type="submit"
      :is-disabled="!isValidForm || !hasEditPermission('ChatGPT')"
      :is-loading="isSubmitting"
      @cancel="onCancel"
    />

    <div v-if="isLoading" class="dd-loader">
      <spinner />
    </div>
  </form>
</template>

<script>
import alertMixin from 'shared/mixins/alertMixin';
import OnlineWaAPI from 'dashboard/api/onlinewa';
import Spinner from 'shared/components/Spinner.vue';
import AttachmentPreview from 'dashboard/components/widgets/AttachmentsPreview';
import FileUpload from 'vue-upload-component';
import { checkFileSizeLimit } from 'shared/helpers/FileHelper';
import hasPermissionMixin from 'dashboard/mixins/hasPermission';

const MAXIMUM_FILE_UPLOAD_SIZE = 40;

export default {
  components: {
    AttachmentPreview,
    FileUpload,
    Spinner,
  },
  mixins: [alertMixin, hasPermissionMixin],
  validations: {},
  data() {
    return {
      freeText: '',
      urls: [],
      attachedFiles: [],
      botName: undefined,
      engine: 'GPT-4',
      isSubmitting: false,
      isLoading: false,
      maxUrls: 1,
      maxFiles: 1,
    };
  },
  computed: {
    engineOptions() {
      return [{ value: 'GPT-4', label: 'GPT-4' }];
    },
    flagItems() {
      return this.$t('D_ASSISTANCE.SETTINGS.FLAGS_ITEMS');
    },
    dropzoneHint() {
      return this.$t('D_ASSISTANCE.SETTINGS.DROPZONE_HINT', {
        size: MAXIMUM_FILE_UPLOAD_SIZE,
      });
    },
    trimmedUrls() {
      return this.urls.map(url => url.trim()).filter(Boolean);
    },
    isValidForm() {
      return (
        !!this.freeText.trim() ||
        this.trimmedUrls.length > 0 ||
        this.attachedFiles.length > 0 ||
        !!this.botName?.trim()
      );
    },
    isAddUrlAvailable() {
      return (
        this.urls.length < this.maxUrls && this.hasEditPermission('ChatGPT')
      );
    },
    isAddFileAvailable() {
      return (
        this.attachedFiles.length < this.maxFiles &&
        this.hasEditPermission('ChatGPT')
      );
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    onIndirectFileUpload(file) {
      if (!file) {
        return;
      }
      if (checkFileSizeLimit(file, MAXIMUM_FILE_UPLOAD_SIZE)) {
        this.attachFile({ file });
      } else {
        this.showAlert(
          this.$t('CONVERSATION.FILE_SIZE_LIMIT', {
            MAXIMUM_FILE_UPLOAD_SIZE,
          })
        );
      }
    },
    attachFile({ file }) {
      const reader = new FileReader();
      reader.readAsDataURL(file.file);
      reader.onloadend = () => {
        if (this.attachedFiles.length < this.maxFiles) {
          this.attachedFiles.push({
            resource: file,
            thumb: reader.result,
          });
        }
      };
    },
    removeAttachment(itemIndex) {
      this.attachedFiles = this.attachedFiles.filter(
        (item, index) => itemIndex !== index
      );
    },

    addUrl() {
      this.urls.push('');
    },
    removeUrl(index) {
      this.urls.splice(index, 1);
    },

    onCancel() {
      this.$emit('cancel');
    },

    async onSubmit() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }
      const payload = {
        free_text: this.freeText?.slice(0, 1000),
        urls_array: this.trimmedUrls,
        files: this.attachedFiles
          ?.filter(x => !!x.resource.file)
          ?.map(x => x.resource.file),
        bot_name: this.botName,
        keep: this.attachedFiles
          ?.filter(x => !x.resource.file && x.key)
          ?.map(x => ({ key: x.key, type: 'file', source: x.resource?.name })),
      };

      this.isSubmitting = true;

      try {
        await OnlineWaAPI.sendSettingToAssistance(payload);
        this.showAlert(this.$t('D_ASSISTANCE.SETTINGS.API.SEND_SUCCESS'));
        this.onCancel();
      } catch (err) {
        this.showAlert(this.$t('PLATFORM.API.SAVE_ERROR'));
      } finally {
        this.isSubmitting = false;
      }
    },
    async load() {
      this.isLoading = true;
      try {
        const { data } = await OnlineWaAPI.getSettingToAssistance();
        const details = data?.details || [];
        this.botName = data?.bot_name;
        this.maxFiles = data?.limits?.max_file_count ?? this.maxFiles;
        this.maxUrls = data?.limits?.max_url_count ?? this.maxUrls;
        this.freeText = details.find(x => x.type === 'text')?.source ?? '';
        const urls = details.filter(x => x.type === 'url').map(x => x.source);
        this.urls = urls.length ? urls : [''];
        this.attachedFiles =
          details
            .filter(x => x.type === 'file')
            ?.map(x => ({
              key: x.key,
              resource: {
                file: undefined,
                name: x.source,
                type: [],
                size: null,
              },
            })) || [];
      } catch (err) {
        this.showAlert(this.$t('PLATFORM.API.GET_ERROR'));
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>
